<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  enabled: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: '',
  },
});

const OPENING =
  'Olá! Eu sou {{nome_atendente}} e vou seguir com o seu atendimento.\n';
const AGENT_TOKEN = /\{\{\s*nome_atendente\s*\}\}/;

const emit = defineEmits(['update:enabled', 'update:message']);
const { t } = useI18n();

const isEnabled = ref(!!props.enabled);
const draftMessage = ref(withAgentToken(props.message));
const touched = ref(false);

if (withAgentToken(props.message) !== (props.message || '').trim()) {
  emit('update:message', withAgentToken(props.message));
}

watch(
  () => props.enabled,
  value => {
    if (touched.value) return;
    isEnabled.value = !!value;
  }
);

watch(
  () => props.message,
  value => {
    if (touched.value) return;
    draftMessage.value = withAgentToken(value);
  }
);

function withAgentToken(value) {
  const text = (value || '').trim();
  if (!text || AGENT_TOKEN.test(text)) return text;
  return `${OPENING}${text}`;
}

function defaultMessage() {
  return t('INBOX_MGMT.SETTINGS_POPUP.ASSIGNMENT_GREETING_DEFAULT');
}

function fillDefault() {
  if (isEnabled.value && !draftMessage.value.trim()) {
    const preset = defaultMessage();
    draftMessage.value = preset;
    emit('update:message', preset);
  }
}

function onEnabledChange() {
  touched.value = true;
  emit('update:enabled', isEnabled.value);
  fillDefault();
}

function onLabelClick() {
  isEnabled.value = !isEnabled.value;
  onEnabledChange();
}

function onMessageInput(event) {
  touched.value = true;
  draftMessage.value = event.target.value;
  emit('update:message', event.target.value);
}
</script>

<template>
  <div class="mt-4 w-full" @click.stop>
    <div class="flex items-center gap-2">
      <input
        v-model="isEnabled"
        type="checkbox"
        @click.stop
        @change="onEnabledChange"
      />
      <span
        class="cursor-pointer text-sm font-medium leading-7 text-n-slate-12"
        @click="onLabelClick"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.ASSIGNMENT_GREETING') }}
      </span>
    </div>
    <p class="pb-1 text-sm not-italic text-n-slate-11">
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.ASSIGNMENT_GREETING_SUB_TEXT') }}
    </p>

    <div v-if="isEnabled" class="w-full pb-2">
      <span class="block text-sm font-medium leading-7 text-n-slate-12">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.ASSIGNMENT_GREETING_MESSAGE') }}
      </span>
      <textarea
        :value="draftMessage"
        rows="5"
        class="!h-auto min-h-[7.5rem]"
        :placeholder="
          $t('INBOX_MGMT.SETTINGS_POPUP.ASSIGNMENT_GREETING_PLACEHOLDER')
        "
        @click.stop
        @input="onMessageInput"
      />
      <p class="pb-1 text-sm not-italic text-n-slate-11">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.ASSIGNMENT_GREETING_HELP') }}
      </p>
    </div>
  </div>
</template>
